import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Popconfirm, Progress, Spin, Typography, message, Upload, Empty } from "antd";
import { DeleteOutlined, InboxOutlined, DownloadOutlined, LinkOutlined, ClockCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import { fetchSession, fetchFiles, initUpload, completeUpload, listUploadParts, getFileDownloadUrl, getStreamDownloadUrl, deletePublicFile } from "../../utils/fileTransferApi";
import { formatFileSize } from "../../utils/fileTransferForm";
import { useTranslation } from "../../i18n";
import { CANCELLED_MESSAGE, UNSUPPORTED_MESSAGE, downloadAllAsZip } from "../../utils/fileTransferDownload";
import "./fileTransfer.css";

const { Title, Paragraph } = Typography;
const { Dragger } = Upload;

const CONCURRENT_UPLOADS = 3;
const MAX_RETRIES = 3;

// 通用延迟工具
function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function FileTransferSession() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadQueue, setUploadQueue] = useState([]);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [zipDownloading, setZipDownloading] = useState(false);
  const [zipProgress, setZipProgress] = useState(null);
  const [timeLeft, setTimeLeft] = useState("");
  const { t } = useTranslation();

  // 加载会话信息
  const loadSession = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchSession(token);
      setSession(data);
      loadFiles();
    } catch (err) {
      if (err.status === 410) {
        setExpired(true);
        setSession(err.data || { title: t("fileTransfer.session.expired") });
      } else {
        setError(err.message || t("fileTransfer.session.loadFailed"));
      }
      setLoading(false);
    }
  }, [token]);

  const loadFiles = useCallback(async () => {
    try {
      const data = await fetchFiles(token);
      setFiles(data.files || []);
      if (data.session) {
        setSession((prev) => ({ ...prev, ...data.session }));
      }
    } catch {
      // Silent - file list is secondary
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // 倒计时
  useEffect(() => {
    if (!session?.expiry) return;
    const interval = setInterval(() => {
      const remaining = session.expiry * 1000 - Date.now();
      if (remaining <= 0) {
        setExpired(true);
        clearInterval(interval);
        return;
      }
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      if (days > 0) {
        setTimeLeft(`${days} 天 ${hours} 小时`);
      } else if (hours > 0) {
        setTimeLeft(`${hours} 小时 ${minutes} 分钟`);
      } else {
        setTimeLeft(`${minutes} 分钟`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [session?.expiry]);

  // ── 带重试机制的单文件 PUT 上传 ──
  const uploadSingleWithRetry = async (file, init, fileId, retriesLeft = MAX_RETRIES) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", init.presignedUrl, true);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setUploadProgress((prev) => ({ ...prev, [fileId]: Math.round((e.loaded / e.total) * 100) }));
      }
    };

    return await new Promise((resolve, reject) => {
      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            await completeUpload(token, { fileId: init.fileId, uploadType: "single" });
            resolve();
          } catch (err) {
            reject(err);
          }
        } else {
          reject(new Error(`HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error(t("fileTransfer.session.networkError")));
      xhr.send(file);
    }).catch(async (err) => {
      if (retriesLeft > 0) {
        await delay(1000);
        return uploadSingleWithRetry(file, init, fileId, retriesLeft - 1);
      }
      throw err;
    });
  };

  // ── 带重试机制的分片上传 ──
  const uploadPartWithRetry = async (part, partBytes, file, init, fileId, retriesLeft = MAX_RETRIES) => {
    const start = (part.partNumber - 1) * partBytes;
    const end = Math.min(start + partBytes, file.size);
    const blob = file.slice(start, end);

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", part.url, true);

    return await new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader("ETag") || "";
          resolve({ partNumber: part.partNumber, etag: etag.replace(/"/g, "") });
        } else {
          reject(new Error(`Part ${part.partNumber} HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error(`Part ${part.partNumber} ${t("fileTransfer.session.networkError")}`));
      xhr.send(blob);
    }).catch(async (err) => {
      if (retriesLeft > 0) {
        await delay(500);
        return uploadPartWithRetry(part, partBytes, file, init, fileId, retriesLeft - 1);
      }
      throw err;
    });
  };

  // ── 完整的上传流程（带重试） ──
  const uploadFile = useCallback(async (file) => {
    const fileId = `file_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));
    setUploadQueue((prev) => [...prev, { fileId, name: file.name, size: file.size }]);

    let lastInit = null;

    try {
      // 1. 初始化上传（带重试）
      let init;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          init = await initUpload(token, {
            filename: file.name,
            fileSize: file.size,
            mimeType: file.type || "application/octet-stream",
          });
          lastInit = init;
          break;
        } catch (err) {
          if (attempt < MAX_RETRIES) {
            await delay(500);
          } else {
            throw err;
          }
        }
      }

      if (init.uploadType === "multipart") {
        // 分片上传（逐片带重试）
        const { uploadId, partCount, parts, partBytes } = init;

        // 断点续传：检查已上传的 parts
        let uploadedParts = [];
        let uploadedEtags = new Map();
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            const partsData = await listUploadParts(token, { fileId: init.fileId, uploadId });
            uploadedParts = (partsData.parts || []).map((p) => p.partNumber);
            uploadedEtags = new Map((partsData.parts || []).map((p) => [p.partNumber, p.etag]));
            break;
          } catch {
            if (attempt < MAX_RETRIES) await delay(500);
          }
        }

        const completedParts = [];
        const uploaded = new Set(uploadedParts);
        let completed = uploaded.size;
        const total = partCount;

        // 并发上传未完成的 parts（逐片自动重试）
        const pending = parts.filter((p) => !uploaded.has(p.partNumber));
        for (let offset = 0; offset < pending.length; offset += CONCURRENT_UPLOADS) {
          const batch = pending.slice(offset, offset + CONCURRENT_UPLOADS);
          const results = await Promise.allSettled(
            batch.map((part) => uploadPartWithRetry(part, partBytes, file, init, fileId))
          );
          for (const result of results) {
            if (result.status === "fulfilled") {
              completedParts.push(result.value);
              completed++;
            } else {
              throw new Error(`${t("fileTransfer.session.partFailed", { msg: result.reason?.message || "" })}`);
            }
          }
          setUploadProgress((prev) => ({ ...prev, [fileId]: Math.round((completed / total) * 100) }));
        }

        // 补上续传过来的 ETag（续传的 part 需要在 complete 时传 etag）
        if (uploaded.size > 0) {
          for (const part of parts) {
            if (uploaded.has(part.partNumber) && !completedParts.find((p) => p.partNumber === part.partNumber)) {
              // 从续传 parts 信息获取 etag——简化为空字符串让 S3 自行处理
              const realEtag = uploadedEtags.get(part.partNumber) || "";
              completedParts.push({ partNumber: part.partNumber, etag: realEtag });
            }
          }
        }

        // 完成 multipart upload
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            await completeUpload(token, {
              fileId: init.fileId,
              uploadType: "multipart",
              uploadId,
              parts: completedParts.sort((a, b) => a.partNumber - b.partNumber),
            });
            break;
          } catch (err) {
            if (attempt < MAX_RETRIES) await delay(1000);
            else throw err;
          }
        }
      } else {
        // 单文件上传（带重试）
        await uploadSingleWithRetry(file, init, fileId);
      }

      setUploadProgress((prev) => ({ ...prev, [fileId]: 100 }));
      message.success(t("fileTransfer.session.uploadSuccess", { name: file.name }));
      loadFiles();
    } catch (err) {
      setUploadProgress((prev) => ({ ...prev, [fileId]: -1 }));
      message.error(t("fileTransfer.session.uploadFailed", { name: file.name, msg: err.message }));
    } finally {
      setUploadQueue((prev) => prev.filter((f) => f.fileId !== fileId));
    }
  }, [token, loadFiles]);

  // 重试上传
  const handleRetry = useCallback(async (fileToRetry) => {
    const file = new File([], fileToRetry.name);
    // 我们不能真正重新创建 File 对象的数据，因为上传失败的文件数据已经不可用了
    // 通知用户重新选择文件
    message.info(t("fileTransfer.session.uploadRetryHint", { name: fileToRetry.name }));
  }, []);

  // 选择文件上传
  const handleUpload = useCallback(async (file) => {
    setUploading(true);
    await uploadFile(file);
    setUploading(false);
    return false;
  }, [uploadFile]);

  // ── 下载处理 ──

  const handleDownload = useCallback(async (file) => {
    if (file.uploadStatus !== "completed") {
      message.warning(t("fileTransfer.session.fileNotReady"));
      return;
    }
    setDownloadingFile(file.id);
    try {
      const downloadUrl = await getFileDownloadUrl(token, file.id);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = file.originalName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      if (file.size > 100 * 1024 * 1024) {
        message.success(t("fileTransfer.session.downloadStart"));
      }
    } catch (err) {
      message.error(t("fileTransfer.session.downloadFailed", { msg: err.message || t("fileTransfer.session.networkError") }));
    } finally {
      setDownloadingFile(null);
    }
  }, [token]);

  const handleStreamDownload = useCallback(async (file) => {

    setDownloadingFile(file.id);
    try {
      const streamUrl = getStreamDownloadUrl(token, file.id);
      const a = document.createElement("a");
      a.href = streamUrl;
      a.download = file.originalName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      message.error(t("fileTransfer.session.streamFailed"));
    } finally {
      setDownloadingFile(null);
    }
  }, [token]);
  const handleDeleteFile = useCallback(async (file) => {
    try {
      await deletePublicFile(token, file.id);
      message.success(t("fileTransfer.session.deleteDone", { name: file.originalName }));
      loadFiles();
    } catch (err) {
      message.error(t("fileTransfer.session.deleteFailed", { msg: err.message || t("fileTransfer.session.networkError") }));
    }
  }, [token, loadFiles]);

  const handleDownloadAll = useCallback(async () => {
    const readyFiles = files.filter((f) => f.uploadStatus === "completed" && f.downloadUrl);
    if (!readyFiles.length) {
      message.warning(t("fileTransfer.session.downloadAllWarn"));
      return;
    }
    setZipDownloading(true);
    setZipProgress({ current: 0, total: readyFiles.length, failedCount: 0 });
    try {
      const entries = readyFiles.map((f) => ({
        ...f,
        proxyDownloadUrl: getStreamDownloadUrl(token, f.id),
      }));
      const result = await downloadAllAsZip(entries, session?.title || "files", {
        onProgress: ({ current, total, name, failedCount }) => {
          setZipProgress({ current, total, name: name || "", failedCount: failedCount || 0 });
        },
      });
      setZipProgress({ current: result.total, total: result.total, done: true, failedCount: result.failed.length });
      if (result.failed.length > 0) {
        message.warning(t("fileTransfer.session.downloadAllPartial", { succeeded: result.succeeded, failed: result.failed.length }));
      } else {
        message.success(t("fileTransfer.session.downloadAllDone"));
      }
    } catch (err) {
      if (err.message === UNSUPPORTED_MESSAGE) {
        message.warning(t("fileTransfer.session.downloadAllUnsupported"));
      } else if (err.message !== CANCELLED_MESSAGE) {
        message.error(t("fileTransfer.session.downloadAllFailed", { msg: err.message }));
      }
    } finally {
      setZipDownloading(false);
      setTimeout(() => setZipProgress(null), 3000);
    }
  }, [files, session?.title, t, token]);


  // ── 渲染 ──

  if (loading) {
    return (
      <>
        <PageTitle><Navigation /></PageTitle>
        <div className="file-transfer-page file-transfer-loading">
          <Spin size="large" />
          <Paragraph type="secondary">{t("fileTransfer.session.loading")}</Paragraph>
        </div>
        <Footer />
      </>
    );
  }

  if (error && !session) {
    return (
      <>
        <PageTitle><Navigation /></PageTitle>
        <div className="file-transfer-page file-transfer-loading">
          <Paragraph type="danger">{error}</Paragraph>
          <Button onClick={() => navigate("/")}>{t("fileTransfer.session.backHome")}</Button>
        </div>
        <Footer />
      </>
    );
  }

  if (expired) {
    return (
      <>
        <PageTitle><Navigation /></PageTitle>
        <div className="file-transfer-page file-transfer-loading">
          <div className="file-transfer-expired-icon">
            <ClockCircleOutlined style={{ fontSize: 48, color: "var(--color-text-muted)" }} />
          </div>
          <Title level={4} style={{ textAlign: "center" }}>{t("fileTransfer.session.expired")}</Title>
          <Paragraph type="secondary" style={{ textAlign: "center" }}>
            {t("fileTransfer.session.expiredDesc", { title: session?.title || t("fileTransfer.session.title") })}
          </Paragraph>
          <Button onClick={() => navigate("/")}>{t("fileTransfer.session.backHome")}</Button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <PageTitle><Navigation /></PageTitle>
      <div className="file-transfer-page">
        {/* 会话信息 */}
        <div className="file-transfer-header">
          <Title level={3} className="file-transfer-title">
            {session?.title || t("fileTransfer.session.title")}
          </Title>
          {session?.description && (
            <Paragraph className="file-transfer-desc">{session.description}</Paragraph>
          )}
          {timeLeft && (
            <div className="file-transfer-countdown">
              <ClockCircleOutlined /> 剩余 {timeLeft}
            </div>
          )}
          <Paragraph type="secondary" className="file-transfer-limits">
            {t("fileTransfer.session.limits", { size: formatFileSize(session?.maxFileSize || 0), count: session?.maxFiles || 0 })}
          </Paragraph>
        </div>

        {/* 上传区域 */}
        <div className="file-transfer-section">
          <Title level={4}>{t("fileTransfer.session.uploadSection")}</Title>
          <Dragger
            accept="*"
            multiple
            showUploadList={false}
            beforeUpload={handleUpload}
            disabled={uploading}
            className="file-transfer-dragger"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t("fileTransfer.session.uploadText")}</p>
            <p className="ant-upload-hint">{t("fileTransfer.session.uploadHint", { size: formatFileSize(session?.maxFileSize || 0) })}</p>
          </Dragger>

          {/* 上传进度（带重试按钮） */}
          {uploadQueue.length > 0 && (
            <div className="file-transfer-upload-queue">
              {uploadQueue.map((item) => {
                const progress = uploadProgress[item.fileId];
                const isFailed = progress === -1;
                return (
                  <div key={item.fileId} className="file-transfer-upload-item">
                    <div className="file-transfer-upload-item__info">
                      <span className="file-transfer-upload-item__name">{item.name}</span>
                      <span className="file-transfer-upload-item__size">{formatFileSize(item.size)}</span>
                    </div>
                    <div className="file-transfer-upload-item__progress-row">
                      <Progress
                        percent={isFailed ? 100 : progress}
                        status={isFailed ? "exception" : progress === 100 ? "success" : "active"}
                        showInfo={!isFailed}
                        format={(pct) => `${pct}%`}
                        className="file-transfer-upload-item__progress"
                      />
                      {isFailed && (
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          onClick={() => handleRetry(item)}
                          className="file-transfer-upload-item__retry"
                        >
                          重试
                        </Button>
                      )}
                    </div>
                    {isFailed && (
                      <Paragraph type="danger" className="file-transfer-upload-item__error">
                        上传失败，请重新选择此文件
                      </Paragraph>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 文件列表 */}
        <div className="file-transfer-section">
          <div className="file-transfer-section-header">
            <Title level={4} style={{ margin: 0 }}>
              {t("fileTransfer.session.fileSection")}
              {files.length > 0 && <span className="file-transfer-count"> ({files.length})</span>}
            </Title>
            {files.filter((f) => f.uploadStatus === "completed" && f.downloadUrl).length > 0 && (
              <Button
                icon={<DownloadOutlined />}
                loading={zipDownloading}
                onClick={handleDownloadAll}
                size="small"
              >
                {t("fileTransfer.session.downloadAll")}
              </Button>
            )}
          </div>
          {zipProgress && (
            <div className="file-transfer-zip-progress">
              <Progress
                percent={Math.round((zipProgress.current / (zipProgress.total || 1)) * 100)}
                status={zipProgress.done ? "success" : "active"}
                showInfo={false}
                size="small"
              />
              <span className="file-transfer-zip-progress__text">
                {zipProgress.done
                  ? (zipProgress.failedCount > 0
                      ? t("fileTransfer.session.downloadAllPartial", {
                          succeeded: (zipProgress.total || 0) - (zipProgress.failedCount || 0),
                          failed: zipProgress.failedCount || 0,
                        })
                      : t("fileTransfer.session.zipDone"))
                  : t("fileTransfer.session.zipProgress", { current: zipProgress.current, total: zipProgress.total }) + ": " + zipProgress.current + "/" + zipProgress.total + (zipProgress.failedCount > 0 ? t("fileTransfer.session.zipProgressFailed", { count: zipProgress.failedCount }) : "") + (zipProgress.name ? " - " + zipProgress.name : "")}
              </span>
            </div>
          )}

          {files.length === 0 ? (
            <Empty description={t("fileTransfer.session.noFiles")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div className="file-transfer-file-list">
              {files.map((file) => (
                <div key={file.id} className="file-transfer-file-item">
                  <div className="file-transfer-file-item__icon">
                    <LinkOutlined />
                  </div>
                  <div className="file-transfer-file-item__info">
                    <div className="file-transfer-file-item__name">{file.originalName}</div>
                    <div className="file-transfer-file-item__meta">
                      {formatFileSize(file.size)}
                      {file.mimeType && <span className="file-transfer-file-item__sep"> | </span>}
                      {file.mimeType}
                    </div>
                  </div>
                  <div className="file-transfer-file-item__actions">
                    <Button
                      type="primary"
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={downloadingFile === file.id}
                      onClick={() => handleDownload(file)}
                      disabled={file.uploadStatus !== "completed"}
                    >
                      下载
                    </Button>
                    {file.size > 100 * 1024 * 1024 && (
                      <Button
                        size="small"
                        loading={downloadingFile === file.id}
                        onClick={() => handleStreamDownload(file)}
                        style={{ marginLeft: 8 }}
                      >
                        流式下载
                      </Button>
                    )}
                    <Popconfirm
                      title={t("fileTransfer.session.deleteConfirm")}
                      description={t("fileTransfer.session.deleteConfirmDesc", { name: file.originalName })}
                      onConfirm={() => handleDeleteFile(file)}
                      okText={t("fileTransfer.manage.deleteOk")}
                      cancelText={t("fileTransfer.manage.deleteCancel")}
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        style={{ marginLeft: 4 }}
                      />
                    </Popconfirm>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default FileTransferSession;
